import {gsap} from "gsap/all";
import $ from "jquery";
import {Gauge} from "../ui/Gauge";

// The ground distance gauge of screen 2: the gradient line the Gauge fills, the
// ship disc riding its leading edge, and the readout travelling under the disc
// with the name of the current phase and the altitude left.
//
// Every state comes from the JSON, either content.game.gauge_start for the top
// of the descent or one of the gauge states of a sequence action
// (gauge_at_sequence_start, the gauge of a press, gauge_at_sequence_end):
//   percent   how far down the line, 0 to 100
//   distance  altitude left, counted up or down to rather than swapped
//   unit      unit that altitude is written in, "mi" or "ft" (default: whatever
//             the markup ships, and whatever the last state named after that)
//   icon      key of one of content.game.gauge_icons; left out keeps the current
//   label     {fr, en} name of the phase
//   duration  travel time in seconds, overriding whatever the game asks for
//   ease      GSAP ease of the travel, "none" for a steady pace
export class GroundDistanceGauge {

  // How many feet one of each unit is, so a state naming another unit carries
  // the count over instead of jumping (1.25 mi becomes 6,600 ft and counts down
  // to 65 from there). A unit not listed here only swaps the label.
  static FEET_PER = {ft: 1, mi: 5280, m: 3.28084, km: 3280.84};

  constructor(el) {
    this.$el = $(el);

    this.gauge = new Gauge(this.$el);

    this.$icons = this.$el.find(".gauge-icon");
    this.$phase = this.$el.find(".ground-distance-gauge-readout .phase");
    this.$distance = this.$el.find(".ground-distance-gauge-readout .value");
    this.$unit = this.$el.find(".ground-distance-gauge-readout .unit");

    // Cross-fade of the ship and the phase name when the state changes, and the
    // tween counting the altitude, tracked so a restart can kill it. The phase
    // name also slides while it fades, out downwards and back in from below, so
    // the new name reads as replacing the old one rather than blinking into it.
    this.swapFadeSec = 0.3;
    this.phaseShiftPx = 30;
    // The name currently written, so a state naming the same one skips the swap.
    // Null rather than empty, an empty name is a state of its own.
    this.phaseText = null;
    this.distanceTween = null;
    this.distance = 0;

    // The unit the readout is counting in, the markup naming the one the run
    // starts on, and how many decimals it writes, which follows the value of
    // the state being travelled to (5 counts whole, 1.25 counts to hundredths).
    this.unit = (this.$unit.text() || "mi").trim();
    this.defaultUnit = this.unit;
    this.decimals = 0;
  }

  // Moves the gauge to a state, over `duration` seconds. Pass 0 to snap, which
  // is what a reset and the start of a run do. Every field is optional, so a
  // partial state only touches what it names and leaves the rest alone.
  setState(state, duration = 1.2) {
    if (!state) return;

    // A state travelled over a long stretch (a whole action, say) reads better
    // at a steady pace, so it can ask for its own ease, "none" being the usual
    // one. The short hops between actions keep the soft settle.
    const ease = state.ease || "power2.inOut";

    this.setIcon(state.icon, duration);
    this.setPhase(state.label, duration);
    this.setDistance(state.distance, duration, ease, state.unit);

    if (state.percent !== undefined) {
      this.gauge.setLevel(state.percent / 100, duration > 0, duration, ease);
    }
  }

  // Shows the ship of that icon key and hides the others. They all sit stacked
  // in the disc, so the swap is only a class away.
  setIcon(icon, duration) {
    if (!icon) return; // a state with no icon of its own keeps the current ship

    const $next = this.$icons.filter(`[data-icon="${icon}"]`);
    if (!$next.length || $next.hasClass("shown")) return;

    const $current = this.$icons.filter(".shown");
    this.$icons.removeClass("shown");
    $next.addClass("shown");

    if (duration <= 0) {
      gsap.set(this.$icons, {autoAlpha: 0});
      gsap.set($next, {autoAlpha: 1});
      return;
    }

    gsap.to($current, {
      autoAlpha: 0,
      duration: this.swapFadeSec,
      ease: "power1.in"
    });
    gsap.fromTo($next, {autoAlpha: 0}, {
      autoAlpha: 1,
      duration: this.swapFadeSec,
      ease: "power1.out"
    });
  }

  // Swaps the phase name: the old one sinks away as it fades out, the text is
  // changed while nothing shows, then the new one rises into place. A state
  // naming no label keeps the current one, pass null to actually clear it.
  setPhase(label, duration) {
    if (label === undefined) return;

    // Several states in a row can carry the same phase name, since only the
    // altitude or the ship changes. Swapping a name for itself would read as a
    // pointless blink, so the name only moves when it actually changes.
    const text = label ? [label.fr || "", label.en || ""].join("|") : "";
    if (text === this.phaseText) return;
    this.phaseText = text;

    const write = () => {
      this.$phase.find(".fr").html(label && label.fr ? label.fr : "");
      this.$phase.find(".en").html(label && label.en ? label.en : "");
    };

    if (duration <= 0) {
      write();
      gsap.set(this.$phase, {
        autoAlpha: 1,
        y: 0
      });
      return;
    }

    // A swap landing while the last one is still running would leave two
    // timelines pulling at the same opacity, so the old one goes first.
    gsap.killTweensOf(this.$phase);

    gsap.timeline()
      .to(this.$phase, {
        autoAlpha: 0,
        y: this.phaseShiftPx,
        duration: this.swapFadeSec,
        ease: "power1.in",
        onComplete: write
      })
      // Straight to, no fromTo: the first leg already left the name faded out
      // and shifted down, and a fromTo would render its start values as soon as
      // the timeline is built, snapping the name away before it can fade.
      .to(this.$phase, {
        autoAlpha: 1,
        y: 0,
        duration: this.swapFadeSec,
        ease: "power1.out"
      });
  }

  // Counts the altitude to its new value instead of swapping it, the way the
  // Path to Mars mission stats do.
  setDistance(distance, duration, ease = "power2.inOut", unit) {
    if (distance === undefined) return;

    if (this.distanceTween) {
      this.distanceTween.kill();
      this.distanceTween = null;
    }

    // Whatever is on screen is carried into the unit of the state first, so the
    // count travels from where it stands rather than jumping to a number of a
    // different size. The decimals are those of the value being counted to.
    this.setUnit(unit);
    this.decimals = window.decimalsOf(distance);

    if (duration <= 0) {
      this.distance = distance;
      this.renderDistance();
      return;
    }

    this.distanceTween = gsap.to(this, {
      distance,
      duration,
      ease,
      onUpdate: () => this.renderDistance()
    });
  }

  // Swaps the unit the readout counts in, converting the altitude currently
  // written into it. A state naming no unit keeps the current one.
  setUnit(unit) {
    if (!unit || unit === this.unit) return;

    const from = GroundDistanceGauge.FEET_PER[this.unit];
    const to = GroundDistanceGauge.FEET_PER[unit];
    if (from && to) this.distance = this.distance * from / to;

    this.unit = unit;
    this.$unit.text(unit);
  }

  // Thousands separated, the way the Gauge readouts are, so a five figure
  // altitude reads at a glance rather than as a run of digits (21,000 mi).
  renderDistance() {
    this.$distance.text(this.distance.toLocaleString("en-US", {
      minimumFractionDigits: this.decimals,
      maximumFractionDigits: this.decimals
    }));
  }

  // Back to the top of the descent, with nothing animating.
  reset(startState) {
    if (this.distanceTween) {
      this.distanceTween.kill();
      this.distanceTween = null;
    }
    gsap.killTweensOf(this.$icons.add(this.$phase));
    // A reset caught mid swap would leave the name faded out and shifted down,
    // and its text half written, so the start state is always rewritten.
    this.phaseText = null;

    // A run that ended in feet goes back to the unit it started on, unless the
    // start state names one of its own.
    this.setUnit((startState && startState.unit) || this.defaultUnit);

    gsap.set(this.$phase, {
      autoAlpha: 1,
      y: 0
    });

    this.setState(startState, 0);
  }
}
